import { getBooleanFromString, getPortal } from '../../../../utils';
import {
  FrameworkResultForm,
  Indicator,
  IndicatorBenchmark,
  IndicatorDimension,
  IndicatorPeriodDate,
  IndicatorTarget,
  MonitoringForm,
} from '../../../../utils/types';
import { client } from '../../../../apollo';
import { gql } from '@apollo/client';
import {
  ALL_FUNDER_PROGRAMMES,
  ALL_MYFORMS,
  ALL_INSTITUTION_PROGRAMMES,
  ALL_SERVICE_PROVIDER_PROGRAMMES,
  DELETE_FRAMEWORKRESULT,
  DELETE_FRAMEWORKRESULTINDICATOR,
  DELETE_MONITORINGFORM,
  DUPLICATE_FRAMEWORKRESULT,
  GET_PROGRAMME,
  PUBLISH_MONITORINGFORM,
  UNPUBLISH_MONITORINGFORM,
  UPSERT_FRAMEWORKRESULT,
  UPSERT_FRAMEWORKRESULTINDICATOR,
  UPSERT_MONITORINGFORM,
  GET_INTERVENTION,
  UPDATE_MONITORINGFORM_CONFIGURE,
  GET_PROGRAMME_MONITORINGFORM,
} from '../../../../graphql';
import { matchPath } from 'react-router';
// import { useRouter } from '../../../../hooks';

export { getNavStyles } from '../../../../components/shared/MultiStep';
// export { ProgrammeStorage } from '../../../../utils';
export { programmeSteps as steps } from '../../../../consts';

export const SUBMIT_ACTION = {
  SaveAndExit: 'saveAndExit',
  Next: 'next',
  Previous: 'previous',
};

export const getUpsertProgrammeOverviewInput = (programme: { id: string; overview: any }, portal: string) => {
  const { id: programmeId, overview } = programme;

  const getGeography = (obj: any) => {
    return {
      id: (obj.id || obj.value || '').startsWith('create') ? '' : obj.value,
      name: obj.name || obj.label,
      fromDate: obj.fromDate,
      toDate: obj.toDate,
      districtId: obj.districtId,
      provinceId: obj.provinceId,
      countryId: obj.countryId,
      localMunicipalityId: obj.localMunicipalityId,
      districtMunicipalityId: obj.districtMunicipalityId,
      cityId: obj.cityId,
    };
  };

  const getReporter = (obj: any) => {
    return {
      id: (obj.id || '').startsWith('create') ? '' : obj.id,
      organisationId: obj.organisation.id,
      assigneeId: obj.assignee ? obj.assignee.id : null,
    };
  };

  const input = {
    portal: portal,
    id: programmeId === '' || programmeId === 'create' ? '' : programmeId,
    name: overview.name,
    summary: overview.summary,
    theoryOfChange: overview.theoryOfChange,
    fromDate: overview.fromDate,
    toDate: overview.toDate,
    isArchived: getBooleanFromString(overview.isArchived ? overview.isArchived.value : 'false'),
    serviceProviderIds: overview.serviceProviderIds ? overview.serviceProviderIds.map((obj: any) => obj.value) : null,
    frameworkTypeId: overview.frameworkTypeId ? overview.frameworkTypeId.value : null,
    geographies: overview.geographies ? overview.geographies.map((obj: any) => getGeography(obj)) : [],
    reporters: overview.reporters ? overview.reporters.map((obj: any) => getReporter(obj)) : [],
    ownerId: overview.ownerId ? overview.ownerId.value : null,
    parentProgrammeId: overview.parentProgrammeId ? overview.parentProgrammeId.value : null,
    portfolioIds: overview.portfolioIds ? overview.portfolioIds.map((obj: any) => obj.value) : [],
    theoryOfChangeFileId: overview.theoryOfChangeFileId ? overview.theoryOfChangeFileId : null,
    budgetAmount: overview.budgetAmount ? overview.budgetAmount : 0,
    currency: overview.currency ? overview.currency.value : '',
    addOnIds: overview.addOnIds ? overview.addOnIds.map((obj: any) => obj.value) : [],
  };

  return input;
};

export const getUpsertProgrammeIndicatorInput = (programme: {
  id: string;
  indicators: any;
  currentIndicators?: any;
}) => {
  const getFrameworkResults = (results: any[]): any[] => {
    const data: {}[] = [];
    results.forEach(result => {
      data.push({
        id: result.id,
        type: result.type.label,
        number: result.number,
        description: result.description,
        levelId: result.type.value,
      });
      data.push(...getFrameworkResults(result.childResults));
    });
    return data;
  };

  // check for differences in frameworkResults type/number/description;
  const results: any[] = getFrameworkResults(programme.indicators.frameworkResults);
  const currentResults: any[] = programme.currentIndicators
    ? getFrameworkResults(programme.currentIndicators.frameworkResults)
    : [];
  const frameworkResults: any[] = [];
  results.forEach(result => {
    const currentResult = currentResults.find(x => x.id === result.id);
    if (!currentResult || JSON.stringify(result) !== JSON.stringify(currentResult)) {
      frameworkResults.push(result);
    }
  });

  if (frameworkResults.length) {
    return {
      id: programme.id,
      frameworkResults,
    };
  }
  return undefined;
};

export const getUpsertProgrammeMonitoringInput = (programme: { id: string; monitoring: any }) => {
  return {
    id: programme.id,
  };
};

export const getUpsertProgrammeInput = (programme: {
  programmeId: string;
  overview: any;
  indicators: any;
  monitoring: any;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getFrameworkResults = (results: FrameworkResultForm[]): any[] => {
    const getIndicators = (indicators: Indicator[]): any[] => {
      const getDimensions = (dimensions: IndicatorDimension[]): any[] => {
        return dimensions.map((dimension, index) => {
          return {
            id: dimension.id.startsWith('create') ? '' : dimension.id,
            index: index,
            name: dimension.name && dimension.name.value,
            options: dimension.options.map(option => option.value),
          };
        });
      };

      const getBenchmarks = (benchmarks: IndicatorBenchmark[]): any[] => {
        return benchmarks.map((benchmark, index) => {
          return {
            id: benchmark.id.startsWith('create') ? '' : benchmark.id,
            index: index,
            name: benchmark.name,
            description: benchmark.description,
          };
        });
      };

      const getPeriods = (periods: IndicatorPeriodDate[]): any[] => {
        return periods.map(period => {
          return {
            id: period.id.startsWith('create') ? '' : period.id,
            periodDate: period.periodDate,
            periodNumber: period.periodNumber,
            label: period.label,
          };
        });
      };

      const getTargets = (targets: IndicatorTarget[]): any[] => {
        return targets.map(target => {
          return {
            id: target.id.startsWith('create') ? '' : target.id,
            value: target.value,
            periodDate: target.periodDate,
            periodNumber: target.periodNumber,
            dimensionOptions: target.dimensionOptions,
          };
        });
      };

      return indicators.map(indicator => {
        const {
          id,
          type,
          period,
          dataSources,
          outcomeTheme,
          dimensions,
          benchmarks,
          periods,
          targets,
          ...rest
        } = indicator;
        return {
          ...rest,
          id: id, //(id || '').startsWith('create') ? '' : id,
          type: (type && type.value) || '',
          outcomeThemeId: outcomeTheme && outcomeTheme.value,
          periodId: period && period.value,
          dataSources: dataSources && dataSources.split('.').forEach(s => s.trim()),
          dimensions: getDimensions(dimensions),
          benchmarks: getBenchmarks(benchmarks),
          periods: getPeriods(periods),
          targets: getTargets(targets),
        };
      });
    };

    return results.map((result: FrameworkResultForm) => {
      return {
        id: (result.id || '').startsWith('create') ? '' : result.id,
        type: (result.type && result.type.value) || '',
        number: result.number,
        description: result.description,
        level: result.level,
        ...(result.childResults &&
          result.childResults.length && { childResults: getFrameworkResults(result.childResults) }),
        indicators: getIndicators(result.indicators),
      };
    });
  };
};

export const generateCreateId = (): String => {
  return 'create' + new Date().getTime().toString();
};

export const clearResultsIds = (results: FrameworkResultForm[]) => {
  var tick: number = new Date().getTime();

  const getCreateId = (): string => {
    return 'create' + (++tick).toString();
  };

  const clearResultsIdsRecurse = (results: FrameworkResultForm[]) => {
    results.forEach(result => {
      result.id = getCreateId();
      result.indicators.forEach(indicator => {
        indicator.id = getCreateId();
        indicator.dimensions.forEach(dim => {
          dim.id = getCreateId();
        });
        indicator.benchmarks.forEach(bm => {
          bm.id = getCreateId();
        });
        indicator.periods.forEach(period => {
          period.id = getCreateId();
        });
        indicator.targets.forEach(target => {
          target.id = getCreateId();
        });
      });
      clearResultsIdsRecurse(result.childResults);
    });
  };

  clearResultsIdsRecurse(results);
};

export const getUpsertFrameworkResultInput = (
  programmeId: string | undefined,
  interventionId: string | undefined,
  parentResultId: string,
  result: FrameworkResultForm
) => {
  return {
    programmeId: programmeId,
    interventionId: interventionId,
    id: result.id,
    type: result.type.label,
    number: result.number,
    description: result.description,
    levelId: result.type.value,
    defaultId: result.defaultId ? result.defaultId : null,
    parentResultId: parentResultId,
  };
};

export const upsertFrameworkResult = async (input: any) => {
  const { data: response } = await client
    .mutate({
      mutation: UPSERT_FRAMEWORKRESULT,
      variables: { input },
      refetchQueries: [
        {
          query: input.programmeId ? GET_PROGRAMME : GET_INTERVENTION,
          variables: { id: input.programmeId || input.interventionId },
        },
      ],
    })
    .then((data: any) => data);
  if (response && response.frameworkResult) {
    return response.frameworkResult.id;
  }
  return undefined;
};

export const deleteFrameworkResult = async (
  frameworkResultId: string,
  programmeId: string | undefined,
  interventionId: string | undefined
) => {
  try {
    await client
      .mutate({
        mutation: DELETE_FRAMEWORKRESULT,
        variables: { id: frameworkResultId },
        refetchQueries: [
          {
            query: programmeId ? GET_PROGRAMME : GET_INTERVENTION,
            variables: { id: programmeId || interventionId },
          },
        ],
      })
      .then((data: any) => data);
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const duplicateFrameworkResult = async (
  frameworkResultId: string,
  programmeId: string | undefined,
  interventionId: string | undefined
) => {
  const { data: response } = await client
    .mutate({
      mutation: DUPLICATE_FRAMEWORKRESULT,
      variables: { id: frameworkResultId },
      refetchQueries: [
        {
          query: programmeId ? GET_PROGRAMME : GET_INTERVENTION,
          variables: { id: programmeId || interventionId },
        },
      ],
    })
    .then((data: any) => data);
  if (response && response.data) {
    return response.data;
  }
  return undefined;
};

export const setDuplicatedIdsFrameworkResult = (
  ids: [{ oldId: string; newId: string }],
  result: FrameworkResultForm
) => {
  const idMap = {};
  ids.forEach(id => {
    idMap[id.oldId] = id.newId;
  });

  const mapResultId = (result: FrameworkResultForm) => {
    result.id = idMap[result.id];
    result.description = `Copy of ${result.description}`;
    result.childResults.forEach(childResult => {
      mapResultId(childResult);
    });
    result.indicators.forEach(indicator => {
      indicator.id = idMap[indicator.id];
      indicator.name = `Copy of ${indicator.name}`;
      indicator.benchmarks.forEach(b => {
        b.id = idMap[b.id];
      });
      indicator.dimensions.forEach(b => {
        b.id = idMap[b.id];
      });
      indicator.periods.forEach(b => {
        b.id = idMap[b.id];
      });
      indicator.targets.forEach(b => {
        b.id = idMap[b.id];
      });
    });
  };

  mapResultId(result);
};

export const getUpsertFrameworkResultIndicatorInput = (resultId: string, indicator: Indicator) => {
  const getDimensions = (dimensions: IndicatorDimension[]): any[] => {
    return dimensions.map((dimension, index) => {
      return {
        id: dimension.id.startsWith('create') ? '' : dimension.id,
        index: index,
        name: dimension.name && dimension.name.value,
        options: dimension.options.map(option => option.value),
      };
    });
  };

  const getBenchmarks = (benchmarks: IndicatorBenchmark[]): any[] => {
    return benchmarks.map((benchmark, index) => {
      return {
        id: benchmark.id.startsWith('create') ? '' : benchmark.id,
        index: index,
        name: benchmark.name,
        description: benchmark.description,
      };
    });
  };

  const getPeriods = (periods: IndicatorPeriodDate[]): any[] => {
    return periods.map(period => {
      return {
        id: period.id.startsWith('create') ? '' : period.id,
        periodDate: period.periodDate,
        periodNumber: period.periodNumber,
        label: period.label,
      };
    });
  };

  const getTargets = (targets: IndicatorTarget[]): any[] => {
    return targets.map(target => {
      return {
        id: target.id.startsWith('create') ? '' : target.id,
        value: target.value,
        periodDate: target.periodDate,
        periodNumber: target.periodNumber,
        dimensionOptions: target.dimensionOptions,
      };
    });
  };

  const getProgressStatusOptions = (progressStatusConditions: any[]): any[] => {
    return progressStatusConditions
      .map(progressStatusOption => {
        if (!(progressStatusOption.status && progressStatusOption.operator)) {
          return undefined;
        }

        return {
          id: progressStatusOption.id.startsWith('create') ? '' : progressStatusOption.id,
          status: progressStatusOption.status && progressStatusOption.status.value,
          index: progressStatusOption.index,
          value1: progressStatusOption.value1,
          value2: progressStatusOption.value2,
          operator: progressStatusOption.operator && progressStatusOption.operator.value,
        };
      })
      .filter((option: any) => option !== undefined);
  };

  const {
    id,
    type,
    period,
    dataSources,
    outcomeTheme,
    dimensions,
    benchmarks,
    periods,
    targets,
    dataSource,
    dataSourceOption,
    dataSourceOptionName,
    calculationMethod,
    baselineOrTargetValue,
    dataSourceWorkspaceId,
    trackingHierarchy,
    trackingHierarchyLevel,
    trackedByOrganisation,
    trackingOrganisation,
    timeline,
    trackProgressStatus,
    statusTier,
    statusType,
    calculateStatus,
    basedOn,
    allStatusOverides,
    progressStatusConditions,
    categories,
    categorySelections,
    ...rest
  } = indicator;

  return {
    frameworkResultId: resultId,
    id: id,
    ...rest,
    type: (type && type.value) || '',
    outcomeThemeId: outcomeTheme && outcomeTheme.value,
    periodId: period && period.value,
    dataSources: dataSources && dataSources.split('.').forEach(s => s.trim()),
    dimensions: getDimensions(dimensions),
    benchmarks: getBenchmarks(benchmarks),
    periods: getPeriods(periods),
    targets: getTargets(targets),
    dataSource: dataSource && dataSource.value,
    dataSourceOption: dataSourceOption && dataSourceOption.value,
    dataSourceOptionName: dataSourceOptionName && dataSourceOptionName.value,
    calculationMethod: calculationMethod && calculationMethod.value,
    baselineOrTargetValue: baselineOrTargetValue && baselineOrTargetValue.value,
    dataSourceWorkspaceId: dataSourceWorkspaceId && dataSourceWorkspaceId.value,
    trackingHierarchy: trackingHierarchy && trackingHierarchy.value,
    trackingHierarchyLevel: trackingHierarchyLevel && trackingHierarchyLevel.value,
    trackingOrganisation: trackingOrganisation && trackingOrganisation.value,
    trackedByOrganisation: trackedByOrganisation,
    timeline: timeline && timeline.value,
    trackProgressStatus: trackProgressStatus,
    statusTier: statusTier && statusTier.value,
    statusType: statusType && statusType.value,
    calculateStatus: calculateStatus,
    basedOn: basedOn && basedOn.value,
    allStatusOverides: allStatusOverides,
    progressStatusConditions: getProgressStatusOptions(progressStatusConditions),
    // Left off entirely when the Categorisation section never loaded, so that callers which don't
    // edit categories can't wipe the ones already saved against the indicator.
    ...(categorySelections && {
      categories: Object.keys(categorySelections).map(groupId => ({
        groupId,
        optionId: categorySelections[groupId] ? categorySelections[groupId]!.value : null,
      })),
    }),
  };
};

export const upsertFrameworkResultIndicator = async (
  input: any,
  programmeId: string | undefined,
  interventionId: string | undefined
) => {
  const { data: response } = await client
    .mutate({
      mutation: UPSERT_FRAMEWORKRESULTINDICATOR,
      variables: { input },
      refetchQueries: [
        {
          query: programmeId ? GET_PROGRAMME : GET_INTERVENTION,
          variables: { id: programmeId || interventionId },
        },
      ],
    })
    .then((data: any) => data);
  if (response && response.indicator) {
    return response.indicator;
  }
  return undefined;
};

export const updateIndicatorIds = (indicator: Indicator, data: any) => {
  const { id, benchmarks: dbBenchmarks, dimensions: dbDimensions, periods: dbPeriods, targets: dbTargets } = data;

  if (!indicator.id) indicator.id = id;

  dbBenchmarks.forEach((dbBenchmark: any) => {
    if (indicator.benchmarks[dbBenchmark.index]) indicator.benchmarks[dbBenchmark.index].id = dbBenchmark.id;
  });
  dbDimensions.forEach((dbDimension: any) => {
    if (indicator.dimensions[dbDimension.index]) indicator.dimensions[dbDimension.index].id = dbDimension.id;
  });
  dbPeriods.forEach((dbPeriod: any) => {
    if (indicator.periods[dbPeriod.periodNumber - 1]) indicator.periods[dbPeriod.periodNumber - 1].id = dbPeriod.id;
  });
  dbTargets.forEach((dbTarget: any) => {
    const target = indicator.targets.find(
      x => x.periodNumber === dbTarget.periodNumber || (x.periodNumber === null && dbTarget.periodNumber === null)
    );
    if (target) target.id = dbTarget.id;
  });
};

export const deleteFrameworkResultIndicator = async (
  indicatorId: string,
  programmeId: string | undefined,
  interventionId: string | undefined
) => {
  try {
    await client
      .mutate({
        mutation: DELETE_FRAMEWORKRESULTINDICATOR,
        variables: { id: indicatorId },
        refetchQueries: [
          {
            query: programmeId ? GET_PROGRAMME : GET_INTERVENTION,
            variables: { id: programmeId || interventionId },
          },
        ],
      })
      .then((data: any) => data);
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const getProgrammeOrganisations = (data: {
  overview: {
    ownerId:
      | {
          value: string;
          label: string;
        }
      | string
      | undefined;
    reporters: {
      organisation: {
        id: string;
      };
    }[];
  };
}): {
  organisations: string[];
  organisationUsers: string[];
} => {
  return {
    organisations: data.overview.reporters
      .map(rep => rep.organisation.id)
      .filter((v, i, a) => {
        return a.indexOf(v) === i;
      }),
    organisationUsers:
      data.overview.ownerId && typeof data.overview.ownerId == 'object' && data.overview.ownerId.value
        ? [data.overview.ownerId.value]
        : [],
  };
};

export const getUpsertMonitoringFormEditInput = (programmeId: string, interventionId: string, form: MonitoringForm) => {
  return {
    programmeId: programmeId,
    interventionId: interventionId,
    id: (form.id || '').startsWith('create') ? '' : form.id,
    name: form.name,
    description: form.description,
    version: form.version,
    published: form.published,
    captureValue: form.captureValue.value,
    periodId: form.period ? form.period.value : '',
    indicators: form.indicators.map(i => {
      return {
        id: (i.id || '').startsWith('create') ? '' : i.id,
        indicatorId: i.indicatorId,
        dimensions: i.dimensions,
        siteId: i.siteId,
      };
    }),
    supportingDocuments: form.supportingDocuments,
  };
};

export const getUpdateMonitoringFormConfigureInput = (form: MonitoringForm) => {
  return {
    id: form.id,
    reporters: form.reporters.map(i => {
      return {
        id: (i.id || '').startsWith('create') ? '' : i.id,
        organisationId: i.organisation.value,
        sites: i.sites,
        // levelId: i.level && i.level.value,
        // siteId: i.site && i.site.value,
      };
    }),
    users: form.users.map(u => {
      return {
        id: (u.id || '').startsWith('create') ? '' : u.id,
        reporterIds: u.reporter.map(r => r.value),
        organisationId: u.organisation.value,
        userId: u.user.value,
        formRoleId: u.formRole.value,
      };
    }),
    dueDates: form.dueDates
      .filter(d => d.date !== null)
      .map(d => {
        return {
          id: (d.id || '').startsWith('create') ? '' : d.id,
          date: d.date,
          periodNumber: d.periodNumber,
        };
      }),
    associatedForms: form.associatedForms.map(f => f.form.value),
    associatedActivities: form.associatedActivities.map(a => a.activity.value),
  };
};

export const upsertMonitoringForm = async (portal: string, input: any) => {
  const { data: response } = await client
    .mutate({
      mutation: UPSERT_MONITORINGFORM,
      variables: { input },
      refetchQueries: [
        {
          query: input.programmeId ? GET_PROGRAMME : GET_INTERVENTION,
          variables: { id: input.programmeId || input.interventionId },
        },
        {
          query: ALL_MYFORMS,
          variables: { input: { portal } },
        },
        {
          query: GET_PROGRAMME_MONITORINGFORM,
          variables: { input: { monitoringFormId: input.id } },
        },
      ],
    })
    .then((data: any) => data);
  if (response && response.monitoringForm) {
    return response.monitoringForm;
  }
  return undefined;
};

export const updateMonitoringFormConfigure = async (
  portal: string,
  programmeId: string,
  interventionId: string,
  input: any
) => {
  const { data: response } = await client
    .mutate({
      mutation: UPDATE_MONITORINGFORM_CONFIGURE,
      variables: { input },
      refetchQueries: [
        {
          query: programmeId ? GET_PROGRAMME : GET_INTERVENTION,
          variables: { id: programmeId || interventionId },
        },
        {
          query: ALL_MYFORMS,
          variables: { input: { portal } },
        },
        {
          query: GET_PROGRAMME_MONITORINGFORM,
          variables: { input: { monitoringFormId: input.id } },
        },
      ],
    })
    .then((data: any) => data);
  if (response && response.monitoringForm) {
    return response.monitoringForm;
  }
  return undefined;
};

export const updateMonitoringFormIds = (form: MonitoringForm, data: any) => {
  const { id, version, users: dbUsers, dueDates: dbDueDates, indicators: dbIndicators } = data;

  form.id = id;
  form.version = version;
  if (dbUsers) {
    dbUsers.forEach((dbUser: any) => {
      form.users.filter(user => user.user.value === dbUser.user.id)[0].id = dbUser.id;
    });
  }
  if (dbDueDates) {
    form.dueDates.forEach(dueDate => {
      if (typeof dueDate.date === 'string') dueDate.date = new Date(dueDate.date);
    });
    dbDueDates.forEach((dbDueDate: any) => {
      form.dueDates.filter(dueDate => dueDate.periodNumber === dbDueDate.periodNumber)[0].id = dbDueDate.id;
    });
  }
  if (dbIndicators) {
    dbIndicators.forEach((dbIndicator: any) => {
      form.indicators.filter(
        indicator =>
          indicator.indicatorId === dbIndicator.indicator.id &&
          indicator.dimensions.toString() === dbIndicator.dimensions.toString()
      )[0].id = dbIndicator.id;
    });
  }
};

export const deleteMonitoringForm = async (portal: string, id: string, programmeId: string, interventionId: string) => {
  try {
    await client
      .mutate({
        mutation: DELETE_MONITORINGFORM,
        variables: { id: id },
        refetchQueries: [
          {
            query: programmeId ? GET_PROGRAMME : GET_INTERVENTION,
            variables: { id: programmeId ? programmeId : interventionId },
          },
          {
            query: ALL_MYFORMS,
            variables: { input: { portal } },
          },
        ],
      })
      .then((data: any) => data);
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const publishMonitoringForm = async (
  portal: string,
  id: string,
  programmeId: string,
  interventionId: string
) => {
  const { data: response } = await client
    .mutate({
      mutation: PUBLISH_MONITORINGFORM,
      variables: { id: id },
      refetchQueries: [
        {
          query: programmeId ? GET_PROGRAMME : GET_INTERVENTION,
          variables: { id: programmeId ? programmeId : interventionId },
        },
        {
          query: ALL_MYFORMS,
          variables: { input: { portal } },
        },
      ],
    })
    .then((data: any) => data);
  if (response && response.monitoringForm) {
    return response.monitoringForm;
  }
  return undefined;
};

export const unpublishMonitoringForm = async (
  portal: string,
  id: string,
  programmeId: string,
  interventionId: string
) => {
  const { data: response } = await client
    .mutate({
      mutation: UNPUBLISH_MONITORINGFORM,
      variables: { id: id },
      refetchQueries: [
        {
          query: programmeId ? GET_PROGRAMME : GET_INTERVENTION,
          variables: { id: programmeId ? programmeId : interventionId },
        },
        {
          query: ALL_MYFORMS,
          variables: { input: { portal } },
        },
      ],
    })
    .then((data: any) => data);
  if (response && response.monitoringForm) {
    return response.monitoringForm;
  }
  return undefined;
};

export const getAllProgrammesQuery = (pathOrPortal: string) => {
  const portal = pathOrPortal[0] === '/' ? getPortal(pathOrPortal) : pathOrPortal;
  switch (portal) {
    case 'funder':
      return ALL_FUNDER_PROGRAMMES;
    case 'service-provider':
      return ALL_SERVICE_PROVIDER_PROGRAMMES;
    case 'institution':
      return ALL_INSTITUTION_PROGRAMMES;
    default:
      return gql``;
  }
};

export const getRootUrl = (path: string): string => {
  if (path === '') {
    // eslint-disable-next-line no-throw-literal
    throw 'Path required';
  }
  const result = matchPath(path, {
    path: '/:portal/programmes',
  });
  if (result) {
    const portal = result.params['portal'];
    return `/${portal}/programmes`;
  }
  return '';
};
